import { useState, useEffect } from "react";
import axios from "axios";
import { Download, LogOut, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("adm_tok") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ count: 0, responses: [] });
  const [selected, setSelected] = useState(null);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("adm_tok", res.data.token);
      setToken(res.data.token);
    } catch (err) {
      setError("Mot de passe incorrect.");
    } finally {
      setLoading(false);
    }
  };

  const fetchResponses = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/responses`, {
        headers: { "X-Admin-Password": token }
      });
      setData(res.data);
    } catch {
      setToken("");
      sessionStorage.removeItem("adm_tok");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchResponses();
  }, [token]);

  const logout = () => {
    sessionStorage.removeItem("adm_tok");
    setToken("");
    setData({ count: 0, responses: [] });
  };

  const csvUrl = `${API}/admin/export.csv?password=${encodeURIComponent(token)}`;

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center px-6" style={{ background: 'var(--brand-bg)', color: 'var(--brand-text)' }}>
        <form onSubmit={login} className="w-full max-w-sm" data-testid="admin-login-form">
          <div className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: 'var(--brand-muted)' }}>Espace privé</div>
          <h1 className="font-serif text-5xl leading-none mb-8">Admin</h1>
          <label className="block text-xs uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--brand-muted)' }}>Mot de passe</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            data-testid="admin-password-input"
            className="w-full bg-transparent border-b py-3 text-lg font-serif outline-none focus:border-[var(--brand-accent)]"
            style={{ borderColor: 'var(--brand-line)' }}
          />
          {error && <div className="mt-3 text-sm" style={{ color: '#b0261a' }}>{error}</div>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-submit-btn"
            className="mt-8 inline-flex items-center gap-3 px-6 py-3 rounded-full text-sm uppercase tracking-[0.2em] disabled:opacity-50"
            style={{ background: 'var(--brand-accent)', color: 'var(--brand-onaccent)' }}
          >
            {loading ? "Connexion..." : "Entrer"} <ChevronRight size={14} />
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen font-sans" style={{ background: 'var(--brand-bg)', color: 'var(--brand-text)' }}>
      <header className="border-b sticky top-0 backdrop-blur-md z-10" style={{ borderColor: 'var(--brand-line)', background: 'color-mix(in srgb, var(--brand-bg) 88%, transparent)' }}>
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.25em]" style={{ color: 'var(--brand-muted)' }}>Tableau de bord</div>
            <div className="font-serif text-2xl">Réponses · <span style={{ color: 'var(--brand-accent)' }}>{data.count}</span></div>
          </div>
          <div className="flex items-center gap-3">
            <a href={csvUrl} data-testid="admin-export-csv-btn" className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs uppercase tracking-[0.2em] border transition-colors hover:bg-[var(--brand-line)]" style={{ borderColor: 'var(--brand-line)' }}>
              <Download size={14} /> Exporter CSV
            </a>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs uppercase tracking-[0.2em] border transition-colors hover:bg-[var(--brand-line)]" style={{ borderColor: 'var(--brand-line)' }}>
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 md:px-12 py-10 grid md:grid-cols-12 gap-8">
        <div className="md:col-span-5">
          <h2 className="font-serif text-3xl mb-4">Toutes les soumissions</h2>
          <div className="space-y-2" data-testid="admin-responses-list">
            {data.responses.length === 0 && (
              <div className="text-sm py-8" style={{ color: 'var(--brand-muted)' }}>Aucune réponse pour l’instant.</div>
            )}
            {data.responses.map((r) => (
              <button
                key={r.id}
                onClick={() => setSelected(r)}
                data-testid={`admin-response-${r.id}`}
                className="w-full text-left p-4 border transition-colors hover:bg-[var(--brand-line)]"
                style={{ borderColor: selected?.id === r.id ? 'var(--brand-accent)' : 'var(--brand-line)' }}
              >
                <div className="flex justify-between items-baseline">
                  <div className="font-serif text-lg">{new Date(r.submitted_at).toLocaleString("fr-FR")}</div>
                  <div className="text-xs uppercase tracking-[0.2em]" style={{ color: 'var(--brand-muted)' }}>{r.id.slice(0, 6)}</div>
                </div>
                <div className="text-sm mt-1 truncate" style={{ color: 'var(--brand-muted)' }}>
                  {r.answers?.transition_phrase || r.answers?.baguette || "—"}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="md:col-span-7">
          <h2 className="font-serif text-3xl mb-4">Détail</h2>
          {!selected && (
            <div className="p-8 border text-sm" style={{ borderColor: 'var(--brand-line)', color: 'var(--brand-muted)' }}>
              Sélectionne une réponse pour voir le détail.
            </div>
          )}
          {selected && (
            <div className="p-6 md:p-8 border space-y-6" style={{ borderColor: 'var(--brand-line)' }} data-testid="admin-response-detail">
              <div className="text-xs uppercase tracking-[0.25em]" style={{ color: 'var(--brand-muted)' }}>
                {new Date(selected.submitted_at).toLocaleString("fr-FR")} · {selected.id}
              </div>
              <div className="grid gap-4">
                {Object.entries(selected.answers || {}).map(([k, v]) => (
                  <div key={k} className="border-b pb-3" style={{ borderColor: 'var(--brand-line)' }}>
                    <div className="text-xs uppercase tracking-[0.2em] mb-1" style={{ color: 'var(--brand-muted)' }}>{k}</div>
                    <div className="text-base font-serif whitespace-pre-wrap">
                      {Array.isArray(v) ? v.join(" · ") : String(v)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
